/**
 * PreparationScreen — Pre-start checklist.
 *
 * UI-INV-005: Leaf component, props only.
 */
import { HearBell } from "../components/HearBell";
import styles from "./PreparationScreen.module.css";
import { TEST_IDS } from "../testing/testIds";
import { PrimaryAction } from "../primitives/PrimaryAction";
import { SecondaryAction } from "../primitives/SecondaryAction";

type Props = {
  onContinue: () => void;
  onSkip: () => void;
  onHearBell?: () => void;
  noblesilence?: boolean;
  whisperFooter?: string;
};

const BASE_CHECKLIST = [
  {
    glyph: "◯",
    title: "Silence notifications",
    body: "do not disturb · until the retreat ends",
  },
  {
    glyph: "◇",
    title: "Prepare your space",
    body: "a cushion, chair, or mat · whatever holds you still",
  },
  {
    glyph: "△",
    title: "Volume up, ringer on",
    body: "bells use media volume · silent/vibrate switch must be off",
  },
  {
    glyph: "✦",
    title: "One breath",
    body: "before you continue · take one breath",
  },
];

const NOBLE_SILENCE_ITEM = {
  glyph: "—",
  title: "Noble silence begins now",
  body: "speech ends here · until the closing ceremony",
};

export function PreparationScreen({
  onContinue,
  onSkip,
  onHearBell,
  noblesilence,
  whisperFooter,
}: Props) {
  const checklist = noblesilence
    ? [...BASE_CHECKLIST, NOBLE_SILENCE_ITEM]
    : BASE_CHECKLIST;

  return (
    <div className={styles.screen} data-testid={TEST_IDS.PREPARATION_SCREEN}>
      <div className={styles.meta}>
        <span className={styles.metaLabel}>Before you sit</span>
      </div>

      <div className={styles.content}>
        <h1 className={styles.heading}>Prepare the room.</h1>

        <div className={styles.list}>
          {checklist.map((item, i) => (
            <div
              key={item.title}
              className={`${styles.item} ${i === checklist.length - 1 ? styles.itemLast : ""}`}
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className={styles.glyph}>{item.glyph}</div>
              <div>
                <div className={styles.itemTitle}>{item.title}</div>
                <div className={styles.itemBody}>{item.body}</div>
              </div>
            </div>
          ))}
        </div>

        {onHearBell && (
          <div className={styles.bellRow}>
            <HearBell onPlay={onHearBell} />
            <div className={styles.bellHint}>this bell marks every transition</div>
          </div>
        )}

        <div className={styles.ctas}>
          <PrimaryAction onClick={onContinue} testId={TEST_IDS.PREPARATION_CONTINUE_BTN}>Continue</PrimaryAction>
          <SecondaryAction onClick={onSkip} testId={TEST_IDS.PREPARATION_SKIP_BTN}>I'm already prepared</SecondaryAction>
        </div>

        {whisperFooter && <p className={styles.whisper}>{whisperFooter}</p>}
      </div>
    </div>
  );
}
